export const insightArticleDraft01 = {
  state: "DRAFT",
  isDraft: true,
  slug: "how-to-research-a-new-b2b-market-before-building-a-prospect-list",
  category: "Market Entry",
  title: "How to research a new B2B market before building a prospect list",
  metaTitle: "How to Research a New B2B Market Before Building a Prospect List | Apping Insights",
  description: "A practical framework for researching a new B2B market before identifying target companies, accounts and stakeholders.",
  standfirst: "Understand the market's commercial structure before choosing companies. A useful prospect list should emerge from that understanding, not replace it.",
  author: "Apping Technology",
  draftLabel: "Internal draft for human review",
  question: "How should a B2B company research a new market?",
  directAnswer: [
    "Start by defining the commercial objective: what the team is selling, the problem it can credibly solve, the territory being considered and the decision the research must support.",
    "Then study the market context, customer and industry structure, routes to market, relevant constraints and the kinds of businesses that could plausibly need the offer. Use that view to build a company landscape and identify accounts worth deeper investigation.",
    "Only after company relevance is established should the team research stakeholders and enrich contact details. The result is not proof of demand. It is a better-grounded set of sales hypotheses to test through conversations and execution.",
  ],
  hero: {
    src: "/images/editorial/cross-border-logistics-port.webp",
    srcSet: "/images/editorial/cross-border-logistics-port-800.webp 800w, /images/editorial/cross-border-logistics-port.webp 1400w",
    alt: "Container ship, cranes and cargo operations at an international commercial port",
    width: 1400,
    height: 788,
    caption: "Illustrative cross-border logistics environment. Photograph by Daniel Miksha, used under the Unsplash License.",
  },
  sections: [
    { id: "why-the-list-should-not-come-first", title: "Why the list should not come first" },
    { id: "start-with-the-commercial-objective", title: "Start with the commercial objective" },
    { id: "understand-the-market-structure", title: "Understand the market structure" },
    { id: "market-attractiveness-and-account-opportunity", title: "Market attractiveness and account opportunity" },
    { id: "understand-how-customers-buy", title: "Understand how customers buy" },
    { id: "build-the-company-landscape", title: "Build the company landscape" },
    { id: "research-the-account-before-the-contact", title: "Research the account before the contact" },
    { id: "identify-the-relevant-people", title: "Identify the relevant people" },
    { id: "what-market-research-cannot-tell-you", title: "What market research cannot tell you" },
    { id: "turn-research-into-a-working-sales-view", title: "Turn research into a working sales view" },
    { id: "practical-checklist", title: "Practical checklist" },
  ],
  blocks: [
    {
      type: "heading",
      id: "why-the-list-should-not-come-first",
      text: "Why the prospect list should not come first",
    },
    {
      type: "paragraph",
      parts: [
        "A long list of companies can create the appearance of progress. It shows that businesses exist, but it does not show that they face the problem you solve, can buy through a workable route, or belong in the territory your team should pursue.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Official export guidance treats market research as part of planning before buyer strategy. It asks teams to examine demand, competition, standards, costs, channels and possible product changes before committing to a market.",
        { text: "", source: 1 },
        " A separate export-planning guide places customer profiles and buyer strategy after the product and market questions have been framed.",
        { text: "", source: 2 },
      ],
    },
    {
      type: "callout",
      title: "Apping analysis",
      text: "Company availability is not the same as account opportunity. A database can tell you that an organization matches a filter. It cannot, by itself, establish that the organization has a relevant need, a reachable buying path or a reason to act now.",
    },
    {
      type: "paragraph",
      parts: [
        "The prospect list should therefore be treated as an output of market understanding. It is a working set of companies produced after the team has made its commercial assumptions explicit, not a substitute for those assumptions.",
      ],
    },
    {
      type: "framework",
      title: "From commercial question to sales action",
      steps: ["Commercial objective", "Market context", "Company landscape", "Relevant accounts", "Stakeholders", "Sales action"],
      caption: "Apping's public conceptual framework. Each stage narrows the next question without exposing internal scoring, source selection or automation logic.",
    },
    {
      type: "heading",
      id: "start-with-the-commercial-objective",
      text: "Start with the commercial objective",
    },
    {
      type: "paragraph",
      parts: [
        "Research becomes useful when it is tied to a decision. A team choosing its first export market needs different evidence from a team validating one segment, recruiting a distributor or preparing an account campaign. The same spreadsheet cannot answer every question.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "At minimum, define what is being sold, the operational or commercial problem it addresses, the geography under consideration, the type of outcome the team wants and the constraints that could make the opportunity impractical. Export-planning guidance similarly starts with product potential, customer profiles, competition, channels and market barriers.",
        { text: "", source: 2 },
      ],
    },
    {
      type: "callout",
      title: "Illustrative scenario",
      text: "Consider a packaging-equipment manufacturer evaluating Malaysia. The useful question is not simply, 'Which manufacturers are in Malaysia?' It is, 'Which types of production operations could need this equipment, can we supply and support them, and what evidence would justify a sales conversation?' This is a hypothetical example, not a customer case.",
    },
    {
      type: "paragraph",
      parts: [
        "That formulation changes the research. It directs attention toward production processes, facility types, installed equipment, service expectations, import conditions, channels and buyer roles. It also makes clear which assumptions still need to be tested.",
      ],
    },
    {
      type: "heading",
      id: "understand-the-market-structure",
      text: "Understand the market structure",
    },
    {
      type: "paragraph",
      parts: [
        "A market is not a flat collection of legal entities. It has industries, subsegments, ownership patterns, supply chains, channels and commercial relationships. The point of structural research is to understand what kinds of businesses actually exist and how they connect to the objective.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Government and intergovernmental tools can help with different parts of that picture. Trade data can indicate the scale and direction of product flows. Market-access tools can surface tariffs, rules of origin and other conditions. Firm-level surveys can add context about the business environment.",
        { text: "", source: 1 },
        { text: "", source: 3 },
        { text: "", source: 5 },
      ],
    },
    {
      type: "paragraph",
      parts: [
        "None of these sources is a complete account map. Their definitions, years and coverage need to be checked. World Bank Enterprise Surveys, for example, are designed around defined formal-firm populations and country survey cycles. Their methodology is valuable precisely because it makes the scope and limits visible.",
        { text: "", source: 5 },
        { text: "", source: 6 },
      ],
    },
    {
      type: "paragraph",
      parts: [
        "For a B2B sales team, the implication is practical: segment the market by differences that could change how customers buy. Depending on the offer, those differences may include use case, operating model, procurement route, installed base, ownership, geography, service requirement or channel dependence. Employee count and industry code may help, but they rarely tell the whole commercial story.",
      ],
    },
    {
      type: "heading",
      id: "market-attractiveness-and-account-opportunity",
      text: "Distinguish market attractiveness from account opportunity",
    },
    {
      type: "paragraph",
      parts: [
        "Market attractiveness describes conditions at a broad level: demand, growth, trade activity, competitive intensity, operating environment and access. Account opportunity asks a different question: which identifiable businesses could plausibly buy from this supplier, through a route the supplier can serve?",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "The two are related but not identical. International market-selection research connects market, network, opportunity and customer choices rather than reducing the decision to one macro ranking.",
        { text: "", source: 4 },
        " A market may look attractive in aggregate while offering few reachable accounts for a particular supplier. The reverse can also occur: a modest market may contain a concentrated cluster of highly relevant buyers.",
      ],
    },
    {
      type: "comparison",
      title: "Two different questions",
      left: {
        heading: "Market attractiveness",
        items: ["Is there relevant economic activity?", "What is the competitive and access environment?", "Can the market justify further investigation?"],
      },
      right: {
        heading: "Account opportunity",
        items: ["Which companies could plausibly need the offer?", "Can the supplier reach and serve them?", "Is there enough evidence for deeper research?"],
      },
      caption: "A conceptual distinction, not a quantitative score or a claim that either side can be evaluated in isolation.",
    },
    {
      type: "paragraph",
      parts: [
        "This distinction prevents a common mistake: translating a large market statistic directly into a large sales pipeline. Macro data can justify where to look. It cannot identify buyer fit, purchase timing or the people who shape a decision.",
      ],
    },
    {
      type: "heading",
      id: "understand-how-customers-buy",
      text: "Understand how customers buy",
    },
    {
      type: "paragraph",
      parts: [
        "The same offer can reach customers through different commercial structures. In one market, a manufacturer may buy directly from an overseas supplier. In another, an importer, distributor, systems integrator, contractor or regional headquarters may control access. Public procurement and regulated sectors can introduce additional requirements.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Market research should therefore investigate buyer types, distribution and service channels, local versus regional decision structures, commercial practices and relevant market-access constraints. The ITA includes channels, standards, duties, shipping, partner margins and product adaptation among the questions exporters should examine.",
        { text: "", source: 1 },
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Internationalisation research also points to recurring barriers involving partners, information, regulation, customs, language, culture and finance.",
        { text: "", source: 7 },
        " These are categories to investigate, not assumptions to apply equally across every country or industry.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "The practical test is not only whether potential customers exist. It is whether the supplier can reach them, transact with them, support them and remain credible after the first sale.",
      ],
    },
    {
      type: "heading",
      id: "build-the-company-landscape",
      text: "Build the company landscape",
    },
    {
      type: "paragraph",
      parts: [
        "Only after the market structure and buying environment are clearer should the team begin assembling companies. Start broadly enough to see the landscape, then narrow according to the commercial question.",
      ],
    },
    {
      type: "landscape",
      title: "From market landscape to people",
      stages: [
        { heading: "Broad universe", text: "Businesses present in the relevant market and value chain." },
        { heading: "Relevant companies", text: "Organizations that fit the segment and commercial context." },
        { heading: "Priority accounts", text: "Companies with enough evidence to justify deeper investigation." },
        { heading: "Stakeholders", text: "People connected to the problem, process and decision." },
      ],
      caption: "The funnel represents increasing research depth. It does not disclose Apping's internal ranking or stakeholder-selection logic.",
    },
    {
      type: "paragraph",
      parts: [
        "A useful company landscape can show categories, relationships and gaps. It may distinguish producers from distributors, local groups from multinational subsidiaries, end users from intermediaries, or central buying teams from individual sites. The dimensions should follow the offer and market, not a universal template.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "At this stage, prioritization should remain explainable. A team should be able to state why a company belongs in the research queue, what evidence supports that view and which uncertainty could remove it. That is more useful than a long list whose inclusion criteria are invisible.",
      ],
    },
    {
      type: "heading",
      id: "research-the-account-before-the-contact",
      text: "Research the account before the contact",
    },
    {
      type: "paragraph",
      parts: [
        "Account research asks whether a specific company fits the commercial hypothesis. Contact enrichment asks for information about people. Those are connected tasks, but they are not interchangeable.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Before collecting names and email addresses at scale, investigate the company's business, operating footprint, products or services, ownership, relevant facilities, recent changes, likely buying process and relationship to the segment. The exact questions depend on what is being sold.",
      ],
    },
    {
      type: "callout",
      title: "Apping analysis",
      text: "Contact data attached to a weak account hypothesis does not become strong sales intelligence. Establish why the company matters first. Then decide which people and details are worth researching.",
    },
    {
      type: "paragraph",
      parts: [
        "This article does not set out a scoring formula or claim that public research reveals intent. The purpose is narrower: make company relevance an explicit question before contact volume becomes the measure of progress.",
      ],
    },
    {
      type: "heading",
      id: "identify-the-relevant-people",
      text: "Then identify the relevant people",
    },
    {
      type: "paragraph",
      parts: [
        "In Apping's view, it is safer to assume that a consequential B2B decision may involve more than one person until account research shows otherwise. The user of an offering, the owner of the business problem, a technical evaluator, procurement, finance and an executive sponsor are all possible participants. Their relevance varies by account and purchase.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "The most senior person is therefore not automatically the correct first contact. A better question is which stakeholder is close enough to the problem to recognize it, credible enough to help evaluate it and connected enough to move the discussion forward.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Keep the stakeholder view tentative. Titles differ across companies, public information is incomplete and influence does not always follow the organization chart. The first conversation should improve the map, not merely execute it.",
      ],
    },
    {
      type: "heading",
      id: "what-market-research-cannot-tell-you",
      text: "What market research cannot tell you",
    },
    {
      type: "paragraph",
      parts: [
        "Research reduces uncertainty. It does not eliminate it. Historical trade data may not reflect a company's current priorities. Public records may omit private operating detail. Firm-level surveys can be representative within their defined universe while still excluding other parts of the economy.",
        { text: "", source: 5 },
        { text: "", source: 6 },
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Desk research cannot reliably prove budget, timing, willingness to change, internal politics, partner quality or buying intent. Nor can it guarantee that the market can be served profitably. Those questions require conversations, commercial testing and operational judgment.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Good research makes these limits visible. It records what is known, what is inferred, what is missing and what the sales team must learn next. False precision is more dangerous than a clearly stated assumption.",
      ],
    },
    {
      type: "heading",
      id: "turn-research-into-a-working-sales-view",
      text: "Turn market research into a working sales view",
    },
    {
      type: "paragraph",
      parts: [
        "The useful output is not a static report followed by an unrelated contact database. It is a connected working view that moves from the commercial objective to market context, customer landscape, relevant companies, account research, stakeholders and sales action.",
      ],
    },
    {
      type: "paragraph",
      parts: [
        "Each sales interaction should then add information. A response may confirm a segment, challenge an assumption, reveal a different stakeholder or show that an account should be deprioritized. The market view becomes stronger when execution feeds learning back into it.",
      ],
    },
    {
      type: "callout",
      title: "The Apping point of view",
      text: "Market research, company discovery and contact discovery should not be treated as independent database exercises. They form a progression from commercial question to sales action, followed by new information that improves the next decision.",
    },
    {
      type: "paragraph",
      parts: [
        "This market-to-account progression is also the problem Apping Sales OS is being designed around: connecting market context, relevant companies, stakeholders and subsequent sales intelligence. The public framework explains the commercial sequence without revealing internal source, ranking or orchestration logic.",
      ],
    },
    {
      type: "heading",
      id: "practical-checklist",
      text: "Practical checklist before building the list",
    },
    {
      type: "checklist",
      items: [
        "What exactly are we trying to sell, and what commercial outcome are we pursuing?",
        "Why might this market be relevant to that objective?",
        "Which types of businesses could plausibly need the offer?",
        "How is the relevant industry, value chain or channel structured?",
        "Which market-access, service or commercial constraints matter?",
        "What does the potential customer landscape look like?",
        "Which companies have enough evidence to deserve deeper investigation?",
        "Who may matter inside those companies, and why?",
        "Which assumptions remain unverified and require a conversation?",
      ],
    },
  ],
  limitations: [
    "No research sequence guarantees market success or buyer demand.",
    "Public sources can be incomplete, differently defined or out of date.",
    "Market-specific trade, regulation, standards and channel information must be refreshed before a commercial decision.",
    "Company fit, stakeholder relevance and buying intent are separate questions.",
  ],
  takeaways: [
    "Define the commercial decision before choosing the dataset.",
    "Treat market attractiveness and account opportunity as related but distinct.",
    "Map customer and channel structure before collecting companies at scale.",
    "Research the account before enriching contacts.",
    "Use conversations to test the assumptions public information cannot resolve.",
  ],
  sources: [
    { id: 1, title: "Conducting Market Research", publisher: "U.S. International Trade Administration", date: "Current guidance, accessed 24 September 2026", url: "https://www.trade.gov/conducting-market-research" },
    { id: 2, title: "Develop an Export Plan", publisher: "U.S. International Trade Administration", date: "Current guidance, accessed 24 September 2026", url: "https://www.trade.gov/develop-export-plan" },
    { id: 3, title: "Market Analysis Tools", publisher: "International Trade Centre", date: "Current portal, accessed 24 September 2026", url: "https://marketanalysis.intracen.org/en/home" },
    { id: 4, title: "International market, network, and opportunity selection: A systematic review of the concepts, themes, and assessment of extant research", publisher: "Journal of International Management", date: "2024", url: "https://doi.org/10.1016/j.intman.2024.101174" },
    { id: 5, title: "Enterprise Surveys data catalog", publisher: "World Bank", date: "Metadata updated 26 November 2024", url: "https://datacatalog.worldbank.org/search/dataset/0037947/enterprise-surveys" },
    { id: 6, title: "Enterprise Surveys Manual and Guide", publisher: "World Bank", date: "2025", url: "https://www.worldbank.org/content/dam/enterprisesurveys/documents/methodology/Enterprise%20Surveys_Manual%20and%20Guide.pdf" },
    { id: 7, title: "Fostering greater SME participation in a globally integrated economy", publisher: "OECD", date: "2019", url: "https://www.oecd.org/en/publications/strengthening-smes-and-entrepreneurship-for-productivity-and-inclusive-growth_c19b6f97-en/full-report/component-7.html" },
  ],
  related: [
    { label: "Explore Apping Market Entry", href: "/market-entry" },
    { label: "See the Apping Sales OS product overview", href: "/product" },
  ],
  cta: {
    title: "Exploring a new B2B market?",
    text: "See how Apping Sales OS approaches the path from market context to relevant companies and stakeholders.",
    href: "/market-entry",
    label: "Explore Market Entry",
    secondary: { href: "/book-a-walkthrough", label: "Book a walkthrough" },
  },
};
